/**
 * A simple LRU (Least Recently Used) cache implementation.
 */
export class LRUCache<K, V> {
    private capacity: number;
    private cache: Map<K, V>;

    /**
     * Creates a new LRU cache.
     * @param capacity The maximum number of items the cache can hold.
     */
    constructor(capacity: number) {
        if (capacity <= 0) {
            throw new Error('Capacity must be a positive number');
        }
        this.capacity = capacity;
        this.cache = new Map<K, V>();
    }

    /**
     * Retrieves an item from the cache.
     * @param key The key to look up.
     * @returns The value associated with the key, or undefined if not found.
     */
    get(key: K): V | undefined {
        const value = this.cache.get(key);
        if (value === undefined) {
            return undefined;
        }
        // Remove and re-add to move to the end (most recently used)
        this.cache.delete(key);
        this.cache.set(key, value);
        return value;
    }

    /**
     * Adds or updates an item in the cache.
     * @param key The key to associate with the value.
     * @param value The value to store.
     */
    put(key: K, value: V): void {
        // If key exists, remove it first
        if (this.cache.has(key)) {
            this.cache.delete(key);
        }
        // If at capacity, remove the oldest item (first in Map)
        else if (this.cache.size >= this.capacity) {
            const firstKey = this.cache.keys().next().value;
            if (firstKey) {
                this.cache.delete(firstKey);
            }
        }

        // Add new item (will be at the end, most recently used)
        this.cache.set(key, value);
    }

    /**
     * Checks if a key exists in the cache.
     * @param key The key to check.
     * @returns True if the key exists, false otherwise.
     */
    has(key: K): boolean {
        return this.cache.has(key);
    }

    /**
     * Removes an item from the cache.
     * @param key The key to remove.
     * @returns True if an item was removed, false otherwise.
     */
    delete(key: K): boolean {
        return this.cache.delete(key);
    }

    /**
     * Clears all items from the cache.
     */
    clear(): void {
        this.cache.clear();
    }

    /**
     * Returns the number of items in the cache.
     */
    get size(): number {
        return this.cache.size;
    }

    /**
     * Returns all keys in the cache.
     * @returns An iterator of all keys.
     */
    keys(): IterableIterator<K> {
        return this.cache.keys();
    }

    /**
     * Returns all values in the cache.
     * @returns An iterator of all values.
     */
    values(): IterableIterator<V> {
        return this.cache.values();
    }
}
